import React, { useState, useRef } from 'react';
import { XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';

interface MultipleImageUploaderProps {
    images: string[];
    onAdd: (file: File) => Promise<void>;
    onRemove: (imageUrl: string) => Promise<void>;
    maxImages?: number;
    maxSizeMB?: number;
}

/**
 * Компонент для загрузки нескольких картинок
 */
export const MultipleImageUploader: React.FC<MultipleImageUploaderProps> = ({
    images,
    onAdd,
    onRemove,
    maxImages = 5,
    maxSizeMB = 50,
}) => {
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [removingUrl, setRemovingUrl] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileInput = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && files.length > 0) {
            await handleFile(files[0]);
        }
        // Сброс значения input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleFile = async (file: File) => {
        setError(null);

        // Проверка лимита
        if (images.length >= maxImages) {
            setError(`Достигнуто максимальное количество изображений: ${maxImages}`);
            return;
        }

        // Проверка типа файла
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            setError('Недопустимый формат файла. Разрешены: JPEG, PNG, WEBP');
            return;
        }

        // Проверка размера
        const maxSize = maxSizeMB * 1024 * 1024;
        if (file.size > maxSize) {
            setError(`Размер файла не должен превышать ${maxSizeMB} МБ`);
            return;
        }

        setUploading(true);
        try {
            await onAdd(file);
        } catch (err: any) {
            setError(err.message || 'Ошибка при загрузке файла');
        } finally {
            setUploading(false);
        }
    };

    const handleRemove = async (imageUrl: string) => {
        setRemovingUrl(imageUrl);
        try {
            await onRemove(imageUrl);
        } catch (err: any) {
            setError(err.message || 'Ошибка при удалении файла');
        } finally {
            setRemovingUrl(null);
        }
    };

    return (
        <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
                Дополнительные изображения ({images.length}/{maxImages})
            </label>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {/* Существующие изображения */}
                {images.map((imageUrl, index) => (
                    <div key={index} className="relative group">
                        <img
                            src={imageUrl}
                            alt={`Доп. изображение ${index + 1}`}
                            className="w-full h-40 object-cover rounded-lg border border-gray-300"
                        />
                        <button
                            onClick={() => handleRemove(imageUrl)}
                            disabled={removingUrl === imageUrl}
                            className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600 opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                            title="Удалить изображение"
                        >
                            {removingUrl === imageUrl ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                            ) : (
                                <XMarkIcon className="h-4 w-4" />
                            )}
                        </button>
                    </div>
                ))}

                {/* Кнопка добавления нового изображения */}
                {images.length < maxImages && (
                    <div
                        onClick={() => fileInputRef.current?.click()}
                        className="w-full h-40 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-gray-400 hover:bg-gray-50 transition-colors"
                    >
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/jpeg,image/jpg,image/png,image/webp"
                            onChange={handleFileInput}
                            className="hidden"
                        />
                        
                        {uploading ? (
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                        ) : (
                            <>
                                <PlusIcon className="h-8 w-8 text-gray-400 mb-2" />
                                <span className="text-sm text-gray-500">Добавить</span>
                            </>
                        )}
                    </div>
                )}
            </div>

            {error && (
                <div className="mt-2 text-sm text-red-600">
                    {error}
                </div>
            )}

            <p className="mt-2 text-xs text-gray-500">
                Форматы: JPEG, PNG, WEBP. Максимальный размер: {maxSizeMB} МБ
            </p>
        </div>
    );
};

